import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import {
  NotificationService,
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_DURATIONS,
  NOTIFICATION_TYPE_SUCCESS,
} from 'ui-components/services/notification-service';
import { defaultStartTime, defaultEndTime, defaultPickupDays } from '../pickup-settings/pickup-settings-day-schedule';
import { WAREHOUSE_SETTINGS_URL } from '../constants';
import { useContextTypes } from 'ui-components/hooks/use-context-types';

import { createLookupHash } from '../../../../services/hash-service';
import useShipFromApi from '../../../../ship-from/hooks/use-ship-from-api';
import * as Api from '../../../../ship-from/services/api';
import useFetchShipFroms from '../use-fetch-ship-froms';
import { editShipFrom as editShipFromAction } from '../../../../store/seller/seller.actions';
import { getShipFromContacts } from '../../../../carrier-pickup/carrier-pickup-api';
import { formatTimeFrom24Hour } from '../../../../services/time-helper-service';
import { useShipFrom } from '../../../../hooks/ship-froms/use-ship-from';
import {
  twelveHourUserSettingsNoTimeZone,
  twelveHourUserSetting,
  twelveHourSettingForTimePicker,
} from '../../../../constants/timezone-format';
import { useUITimeFormatSetting } from '../../../../hooks/use-user-setting';
import { useYupFormValidation } from '../../../../validation/use-yup-form-validation';
import pickupSettingsValidationSchema from '../validation-schemas/pickup-settings-validation-schema';

import { addressPropType } from '../../../../address/address-fields/address-prop-type';
import { ChildrenPropType } from '../../../../prop-types/children';
import { PickupContactPropType } from '../../../../prop-types/pickup-contact';
import { PickupSettingsDayPropType } from '../../../../prop-types/pickup-settings-day';
import useDebounce from '../use-debounce';
import messages from '../messages';

const PickupSettingsContext = React.createContext();
export const usePickupSettingsContextTypes = (ComponentName) =>
  useContextTypes(ComponentName, PickupSettingsContext, {
    hasCarrierInstructionError: PropTypes.bool.isRequired,
    onUpdateInstructions: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    toggleDayChecked: PropTypes.func.isRequired,
    updateStartTimeForDay: PropTypes.func.isRequired,
    updateEndTimeForDay: PropTypes.func.isRequired,
    setStartTime: PropTypes.func.isRequired,
    setEndTime: PropTypes.func.isRequired,
    setPreferredTime: PropTypes.func.isRequired,
    refreshContactList: PropTypes.func.isRequired,
    onSelectContact: PropTypes.func.isRequired,
    isSameSchedule: PropTypes.bool.isRequired,
    onSetPickPackTime: PropTypes.func.isRequired,
    contacts: PropTypes.arrayOf(PickupContactPropType).isRequired,
    selectedContact: PickupContactPropType,
    shipFrom: PropTypes.shape({
      shipFromId: PropTypes.string,
      name: PropTypes.string,
      originAddress: addressPropType,
    }).isRequired,
    daySchedule: PropTypes.objectOf(PickupSettingsDayPropType).isRequired,
    timeFormatToUse: PropTypes.string.isRequired,
  });
export const usePickupSettingsContext = () => useContext(PickupSettingsContext);

const CONTACT_NOTIFICATION_ERROR = {
  notificationType: NOTIFICATION_TYPE_ERROR,
  title: <FormattedMessage {...messages.failedShipFromContactsTitle} />,
  content: <FormattedMessage {...messages.failedShipFromContactsContent} />,
  duration: NOTIFICATION_DURATIONS.SHORT,
};

const SAVE_NOTIFICATION_SUCCESS = {
  title: '',
  notificationType: NOTIFICATION_TYPE_SUCCESS,
  content: <FormattedMessage {...messages.editShipFromSuccessMessageTitle} />,
  duration: NOTIFICATION_DURATIONS.SHORT,
};

const SAVE_NOTIFICATION_ERROR = {
  title: '',
  notificationType: NOTIFICATION_TYPE_ERROR,
  content: <FormattedMessage {...messages.failedToSaveShipFrom} />,
  duration: NOTIFICATION_DURATIONS.SHORT,
};

const PickupSettingsContextProvider = ({ children, shipFromId = '' }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const editedShipFrom = useShipFrom(shipFromId);
  const { fetchShipFroms } = useFetchShipFroms();

  const onCancel = useCallback(() => history.push(WAREHOUSE_SETTINGS_URL), [history]);
  const { pickupSettings } = editedShipFrom;

  const daysFromShipFrom = pickupSettings?.days
    ? pickupSettings.days.map((day) => ({
        ...day,
        startTime: formatTimeFrom24Hour(day.startTime, twelveHourSettingForTimePicker),
        endTime: formatTimeFrom24Hour(day.endTime, twelveHourSettingForTimePicker),
        preferredTime: formatTimeFrom24Hour(day.preferredTime, twelveHourSettingForTimePicker),
        isChecked: true,
      }))
    : [];
  const shipFromDaySettings = daysFromShipFrom[0] || {};
  const mappedDefaultPickupDays = pickupSettings?.days
    ? defaultPickupDays.map((day) => ({
        ...day,
        pickPackTime: shipFromDaySettings.pickPackTime,
        preferredTime: shipFromDaySettings.preferredTime,
        instructions: shipFromDaySettings.instructions,
        shipFromContactId: shipFromDaySettings.shipFromContactId,
        isChecked: false,
      }))
    : defaultPickupDays;
  const daysToRenderMap = createLookupHash([...mappedDefaultPickupDays, ...daysFromShipFrom], 'day');

  const startTimeInitialState = daysFromShipFrom[0]?.startTime || defaultStartTime;
  const endTimeInitialState = daysFromShipFrom[0]?.endTime || defaultEndTime;
  const isSameScheduleInitialState = daysFromShipFrom.reduce(
    (acc, cur) => acc && cur.startTime === startTimeInitialState && cur.endTime === endTimeInitialState,
    true,
  );

  const [hasCarrierInstructionError, setHasCarrierInstructionError] = useState(false);
  const [contacts, setContacts] = useAsyncSafeState([]);
  const [selectedContact, setSelectedContact] = useAsyncSafeState(null);
  const [isSameSchedule, setIsSameSchedule] = useState(isSameScheduleInitialState);
  const [daySchedule, setDaySchedule] = useState(daysToRenderMap);
  const { callShipFromApi: editShipFrom } = useShipFromApi(Api.editShipFrom);

  const { errors: validationErrors, isValid } = useYupFormValidation({
    form: Object.values(daySchedule),
    schema: pickupSettingsValidationSchema,
  });

  const toggleDayChecked = useCallback(
    (day) => {
      const dayToUpdate = { ...daySchedule[day] };
      dayToUpdate.isChecked = !dayToUpdate.isChecked;
      setDaySchedule({ ...daySchedule, [day]: dayToUpdate });
    },
    [daySchedule],
  );

  const updateStartTimeForDay = useCallback(
    (day, time) => {
      const dayToUpdate = { ...daySchedule[day] };
      dayToUpdate.startTime = time;
      setDaySchedule({ ...daySchedule, [day]: dayToUpdate });
    },
    [daySchedule],
  );

  const updateEndTimeForDay = useCallback(
    (day, time) => {
      const dayToUpdate = { ...daySchedule[day] };
      dayToUpdate.endTime = time;
      setDaySchedule({ ...daySchedule, [day]: dayToUpdate });
    },
    [daySchedule],
  );

  const onUpdateInstructions = useCallback((instructions) => {
    setDaySchedule((schedule) => {
      const newDaySchedule = Object.values(schedule).map((day) => ({
        ...day,
        instructions,
      }));
      return createLookupHash(newDaySchedule, 'day');
    });
    setHasCarrierInstructionError(instructions.length > 250);
  }, []);

  const setStartTime = useCallback((startTime) => {
    setDaySchedule((schedule) => {
      const newDaySchedule = Object.values(schedule).map((day) => ({
        ...day,
        startTime,
      }));
      return createLookupHash(newDaySchedule, 'day');
    });
  }, []);

  const setEndTime = useCallback((endTime) => {
    setDaySchedule((schedule) => {
      const newDaySchedule = Object.values(schedule).map((day) => ({
        ...day,
        endTime,
      }));
      return createLookupHash(newDaySchedule, 'day');
    });
  }, []);

  const toggleIsSameSchedule = useCallback(() => {
    if (!isSameSchedule) {
      setStartTime(daySchedule.Monday.startTime);
      setEndTime(daySchedule.Monday.endTime);
    }
    setIsSameSchedule((isSame) => !isSame);
  }, [daySchedule.Monday.endTime, daySchedule.Monday.startTime, isSameSchedule, setEndTime, setStartTime]);

  const setPreferredTime = useCallback((preferredTime) => {
    setDaySchedule((schedule) => {
      const newDaySchedule = Object.values(schedule).map((day) => ({
        ...day,
        preferredTime,
      }));
      return createLookupHash(newDaySchedule, 'day');
    });
  }, []);

  const onSetPickPackTime = useCallback((pickPackTime) => {
    setDaySchedule((schedule) => {
      const newDaySchedule = Object.values(schedule).map((day) => ({
        ...day,
        pickPackTime,
      }));
      return createLookupHash(newDaySchedule, 'day');
    });
  }, []);

  const debouncedDaySchedule = useDebounce(daySchedule, 500);

  const refreshContactList = useEffect(() => {
    getShipFromContacts(shipFromId)
      .then(({ data = [] }) => {
        setContacts(data);
        const day = Object.values(debouncedDaySchedule)[0];
        if (day && !selectedContact) {
          const contactFromDay = data.find(({ shipFromContactId }) => shipFromContactId === day.shipFromContactId);
          setSelectedContact(contactFromDay);
        }
      })
      .catch(() => {
        NotificationService.showNotification(CONTACT_NOTIFICATION_ERROR);
      });
  }, [shipFromId, setContacts, debouncedDaySchedule, selectedContact, setSelectedContact]);

  const onSelectContact = useCallback(
    (contact) => {
      if (contact) {
        setDaySchedule((schedule) => {
          const newDaySchedule = Object.values(schedule).map((day) => ({
            ...day,
            shipFromContactId: contact.shipFromContactId,
          }));
          return createLookupHash(newDaySchedule, 'day');
        });
        setSelectedContact(contact);
        const isContactInList = !!contacts.find(({ shipFromContactId }) => shipFromContactId === contact.shipFromContactId);
        if (!isContactInList) {
          refreshContactList();
        }
      }
    },
    [contacts, refreshContactList, setSelectedContact],
  );

  const buildDaysForPickupSettings = useCallback(() => {
    return Object.values(daySchedule)
      .filter(({ isChecked }) => isChecked)
      .map((day) => ({
        ...day,
        shipFromContactId: selectedContact?.shipFromContactId,
      }));
  }, [daySchedule, selectedContact?.shipFromContactId]);

  const onSave = useCallback(async () => {
    const days = buildDaysForPickupSettings();
    try {
      const result = await editShipFrom({
        ...editedShipFrom,
        pickupSettings: { days },
      });
      dispatch(editShipFromAction(result));
      fetchShipFroms();
      NotificationService.showNotification(SAVE_NOTIFICATION_SUCCESS);
    } catch {
      NotificationService.showNotification(SAVE_NOTIFICATION_ERROR);
    }
  }, [buildDaysForPickupSettings, editShipFrom, editedShipFrom, dispatch, fetchShipFroms]);

  const userTimeFormat = useUITimeFormatSetting();
  const timeFormatToUse = userTimeFormat === twelveHourUserSetting ? twelveHourUserSettingsNoTimeZone : userTimeFormat;

  const value = useMemo(
    () => ({
      hasCarrierInstructionError,
      onUpdateInstructions,
      onSetPickPackTime,
      onSave,
      onCancel,
      toggleDayChecked,
      updateStartTimeForDay,
      updateEndTimeForDay,
      setStartTime,
      setEndTime,
      setPreferredTime,
      refreshContactList,
      onSelectContact,
      isSameSchedule,
      toggleIsSameSchedule,
      contacts,
      selectedContact,
      shipFrom: editedShipFrom,
      daySchedule,
      timeFormatToUse,
      validationErrors,
      isValid,
    }),
    [
      hasCarrierInstructionError,
      onUpdateInstructions,
      onSetPickPackTime,
      onSave,
      onCancel,
      toggleDayChecked,
      updateStartTimeForDay,
      updateEndTimeForDay,
      setStartTime,
      setEndTime,
      setPreferredTime,
      refreshContactList,
      onSelectContact,
      isSameSchedule,
      toggleIsSameSchedule,
      contacts,
      selectedContact,
      editedShipFrom,
      daySchedule,
      timeFormatToUse,
      validationErrors,
      isValid,
    ],
  );

  return <PickupSettingsContext.Provider value={value}>{children}</PickupSettingsContext.Provider>;
};

PickupSettingsContextProvider.propTypes = {
  children: ChildrenPropType.isRequired,
  shipFromId: PropTypes.string,
};

export default PickupSettingsContextProvider;
